import { useMemo } from 'react';

import type { KeyedMutator, SWRConfiguration } from 'swr';
import useSWR from 'swr';

import { AnalyticsDataService } from '@/utils/services/analytics/AnalyticsDataService';

import { useIsFirstStatsLoaded } from '@/hooks/socialprofiles/useIsFirstStatsLoaded';

import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { MEDIA_ANALYTICS_V2 } from '@/lib/routes/apiEndpoints';

/**
 * Media analytics response from the API
 * Fields represent X (Twitter) media engagement metrics
 */
type MediaAnalytics = {
    retweet_count?: number;
    retweet_count_organic?: number;
    retweet_count_paid?: number;
    reply_count?: number;
    reply_count_organic?: number;
    reply_count_paid?: number;
    like_count?: number;
    like_count_organic?: number;
    like_count_paid?: number;
    impression_count?: number;
    impression_count_organic?: number;
    impression_count_paid?: number;
    quote_count?: number;
    bookmark_count?: number;
    engagements?: number;
    video_view_count?: number;
    video_view_count_organic?: number;
    video_view_count_paid?: number;
    playback_0_count?: number;
    playback_25_count?: number;
    playback_50_count?: number;
    playback_75_count?: number;
    playback_100_count?: number;
};

type UseMediaAnalyticsParams = {
    mediaId?: number | string;
    socialProfileId?: number;
    requiredFields?: string[];
    startDate?: string | Date;
    endDate?: string | Date;
    shouldFetch?: boolean;
    options?: SWRConfiguration<MediaAnalytics, Error>;
};

type UseMediaAnalyticsReturn = {
    analytics: MediaAnalytics | undefined;
    mutateAnalytics: KeyedMutator<MediaAnalytics>;
    analyticsError: Error | undefined;
    isValidatingAnalytics: boolean;
    isLoading: boolean;
};

/**
 * Hook for lazy loading analytics for a single media item
 * Uses MEDIA_ANALYTICS_V2 endpoint: /v2/social-profile/{SPID}/medias/{mediaID}/analytics
 */
const useMediaAnalytics = ({
    mediaId,
    socialProfileId,
    requiredFields = [],
    startDate,
    endDate,
    shouldFetch = false,
    options = {}
}: UseMediaAnalyticsParams = {}): UseMediaAnalyticsReturn => {
    const isFirstStatsLoaded = useIsFirstStatsLoaded({ SPID: socialProfileId });

    // Build request params - memoized to prevent unnecessary recalculations
    const requestParams = useMemo(() => {
        if (!mediaId || !socialProfileId || !requiredFields.length) {
            return null;
        }
        return AnalyticsDataService.buildMediaAnalyticsParams({
            mediaId,
            requiredFields,
            startDate,
            endDate
        });
    }, [mediaId, socialProfileId, requiredFields, startDate, endDate]);

    // Build SWR key - when this changes, SWR automatically refetches
    const swrKey = useMemo(() => {
        if (!shouldFetch || !isFirstStatsLoaded || !requestParams || !mediaId) {
            return null;
        }
        return [
            injectEndpointUrlParams(MEDIA_ANALYTICS_V2, {
                SPID: socialProfileId,
                mediaID: mediaId
            }),
            JSON.stringify(requestParams) // Stringify to ensure key stability
        ];
    }, [shouldFetch, isFirstStatsLoaded, requestParams, mediaId, socialProfileId]);

    const {
        data,
        mutate: mutateAnalytics,
        isValidating: isValidatingAnalytics,
        error: analyticsError
    } = useSWR<MediaAnalytics, Error>(
        swrKey,
        ([url]) => get<{ data: MediaAnalytics }>(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    return {
        analytics: data,
        mutateAnalytics,
        analyticsError,
        isValidatingAnalytics,
        isLoading: !!swrKey && !data && !analyticsError
    };
};

export default useMediaAnalytics;
